import { useEffect, useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import {
  Plus, Trash2, Upload, X, Image as ImageIcon, Eye, FolderOpen,
  CheckCircle2, AlertCircle, Loader2, ChevronLeft, ChevronRight
} from "lucide-react";
import api, { BACKEND } from "@/lib/api";
import { AdminModal } from "@/components/AdminModal";
import { Header } from "./Schools";

const resolve = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND}${u}`);
const ALLOWED_EXT = ["jpg", "jpeg", "png", "webp"];
const SI = "px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm w-full focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10";

const EMPTY_FORM = {
  school_name: "", school_logo: "", training_name: "",
  date: "", location: "", teacher_count: 0, cover_image: "",
};

// ── Lightbox ──────────────────────────────────────────────────────────────────
function Lightbox({ photos, idx, onClose }) {
  const [cur, setCur] = useState(idx);
  const prev = useCallback(() => setCur((i) => (i > 0 ? i - 1 : photos.length - 1)), [photos.length]);
  const next = useCallback(() => setCur((i) => (i < photos.length - 1 ? i + 1 : 0)), [photos.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, prev, next]);

  if (!photos[cur]) return null;
  return (
    <div
      className="fixed inset-0 z-[200] bg-black/95 flex items-center justify-center"
      onClick={onClose}
      data-testid="gallery-lightbox"
    >
      <button onClick={onClose} className="absolute top-4 right-4 w-10 h-10 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white" aria-label="Close" data-testid="lightbox-close">
        <X className="w-5 h-5" />
      </button>
      <button onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white" aria-label="Previous" data-testid="lightbox-prev">
        <ChevronLeft className="w-5 h-5" />
      </button>
      <img
        src={resolve(photos[cur])}
        alt=""
        onClick={(e) => e.stopPropagation()}
        className="max-w-[90vw] max-h-[88vh] object-contain rounded-lg"
        data-testid="lightbox-image"
      />
      <button onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white" aria-label="Next" data-testid="lightbox-next">
        <ChevronRight className="w-5 h-5" />
      </button>
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-xs font-mono">
        {cur + 1} / {photos.length}
      </div>
    </div>
  );
}

// ── Multi-file upload zone ─────────────────────────────────────────────────────
function PhotoUploadZone({ albumId, onUploaded }) {
  const fileRef = useRef();
  const [files, setFiles] = useState([]);     // { file, status, url, error }
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const addFiles = (incoming) => {
    const valid = Array.from(incoming).filter((f) => {
      const ext = f.name.split(".").pop().toLowerCase();
      return ALLOWED_EXT.includes(ext);
    });
    if (valid.length < incoming.length) {
      toast.error(`Only ${ALLOWED_EXT.join(", ")} files are accepted.`);
    }
    setFiles((prev) => [
      ...prev,
      ...valid.map((f) => ({ file: f, status: "pending", url: null, error: null })),
    ]);
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  const removeFile = (i) => setFiles((prev) => prev.filter((_, j) => j !== i));

  const upload = async () => {
    if (!files.length) return;
    const pending = files.filter((f) => f.status === "pending");
    if (!pending.length) { toast.info("No new files to upload."); return; }
    setUploading(true);
    const fd = new FormData();
    pending.forEach(({ file }) => fd.append("files", file));
    try {
      const { data } = await api.post(`/gallery/${albumId}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFiles((prev) =>
        prev.map((item) => {
          if (item.status !== "pending") return item;
          const idx = pending.indexOf(item);
          const url = data.photos?.[idx];
          return url
            ? { ...item, status: "done", url }
            : { ...item, status: "error", error: "Upload failed" };
        })
      );
      toast.success(`${data.photos?.length || 0} photo(s) uploaded.`);
      onUploaded();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
      setFiles((prev) => prev.map((item) => item.status === "pending" ? { ...item, status: "error", error: "Failed" } : item));
    }
    setUploading(false);
  };

  return (
    <div className="space-y-4">
      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileRef.current.click()}
        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${dragOver ? "border-[#4F46E5] bg-indigo-50" : "border-gray-200 hover:border-gray-300 bg-gray-50"}`}
        data-testid="photo-dropzone"
      >
        <ImageIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
        <p className="text-sm font-medium text-gray-700">Drop photos here or click to browse</p>
        <p className="text-xs text-gray-400 mt-1">JPG, JPEG, PNG, WEBP · Max 15 MB each</p>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          className="hidden"
          onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
          data-testid="photo-file-input"
        />
      </div>

      {/* Preview queue */}
      {files.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="photo-preview-queue">
          {files.map((item, i) => (
            <div key={i} className="relative group rounded-lg overflow-hidden border border-gray-200 aspect-square bg-gray-100">
              <img
                src={item.url ? resolve(item.url) : URL.createObjectURL(item.file)}
                alt=""
                className="w-full h-full object-cover"
              />
              {/* Status overlay */}
              {item.status === "pending" && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-colors">
                  <button
                    onClick={() => removeFile(i)}
                    className="w-6 h-6 grid place-items-center rounded-full bg-white/90 text-gray-700 opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Remove"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
              {item.status === "done" && (
                <div className="absolute bottom-1 right-1 w-5 h-5 grid place-items-center rounded-full bg-emerald-500">
                  <CheckCircle2 className="w-3 h-3 text-white" />
                </div>
              )}
              {item.status === "error" && (
                <div className="absolute bottom-1 right-1 w-5 h-5 grid place-items-center rounded-full bg-rose-500">
                  <AlertCircle className="w-3 h-3 text-white" />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3 pt-1">
        <button
          onClick={upload}
          disabled={uploading || !files.some((f) => f.status === "pending")}
          className="inline-flex items-center gap-2 bg-[#4F46E5] hover:bg-[#4338CA] disabled:opacity-50 text-white text-sm font-medium px-4 py-2.5 rounded-lg transition-colors"
          data-testid="upload-submit-btn"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploading ? "Uploading…" : `Upload ${files.filter((f) => f.status === "pending").length || ""} Photo${files.filter((f) => f.status === "pending").length !== 1 ? "s" : ""}`}
        </button>
        {files.length > 0 && (
          <button
            onClick={() => setFiles([])}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Clear all
          </button>
        )}
      </div>
    </div>
  );
}

// ── Google Drive Photo Browser (same pattern as AdminAchievements) ────────────
function DrivePhotoBrowser({ albumId, onUploaded }) {
  const [folderUrl, setFolderUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [error, setError] = useState("");
  const [adding, setAdding] = useState(false);

  async function fetchFolder() {
    if (!folderUrl.trim()) return;
    setLoading(true); setError(""); setFiles([]);
    try {
      const { data } = await api.post("/gallery/drive-folder", { folder_url: folderUrl.trim() });
      setFiles(data.files || []);
      if (!data.files?.length) setError("No images found. Make sure the folder is public and contains images.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not fetch folder. Ensure it is shared as 'Anyone with the link'.");
    } finally { setLoading(false); }
  }

  function toggleSelect(file) {
    setSelected(prev => {
      const n = new Set(prev);
      n.has(file.id) ? n.delete(file.id) : n.add(file.id);
      return n;
    });
  }

  async function addSelected() {
    const urls = files.filter(f => selected.has(f.id)).map(f => f.direct_url);
    if (!urls.length) { toast.error("Select at least one image."); return; }
    setAdding(true);
    try {
      await api.post(`/gallery/${albumId}/photos/urls`, { urls });
      toast.success(`${urls.length} photo${urls.length > 1 ? "s" : ""} added from Drive.`);
      setSelected(new Set());
      onUploaded();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add photos.");
    } finally { setAdding(false); }
  }

  // Single Drive link mode
  const [singleUrl, setSingleUrl] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState(false);

  function previewSingle() {
    const raw = singleUrl.trim();
    if (!raw) return;
    // Extract file ID from various Drive URL formats
    const m = raw.match(/\/d\/([a-zA-Z0-9_-]+)/);
    if (!m) { toast.error("Could not extract file ID from URL."); return; }
    const directUrl = `https://lh3.googleusercontent.com/d/${m[1]}`;
    setPreview(directUrl);
    setPreviewErr(false);
  }

  async function addSingle() {
    if (!preview) return;
    setAdding(true);
    try {
      await api.post(`/gallery/${albumId}/photos/urls`, { urls: [preview] });
      toast.success("Photo added from Drive.");
      setSingleUrl(""); setPreview(null);
      onUploaded();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add photo.");
    } finally { setAdding(false); }
  }

  return (
    <div className="space-y-5">
      {/* Single file link */}
      <div className="space-y-3 rounded-xl border border-blue-100 bg-blue-50/40 p-4">
        <label className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
          <ImageIcon className="w-3.5 h-3.5 text-blue-500" /> Single Drive Image
        </label>
        <p className="text-[10px] text-gray-400">Paste a Google Drive file link. The file must be shared as "Anyone with the link can view".</p>
        <div className="flex gap-2">
          <input className={SI} value={singleUrl} onChange={e => setSingleUrl(e.target.value)}
            placeholder="https://drive.google.com/file/d/.../view"
            onKeyDown={e => e.key === "Enter" && previewSingle()} />
          <button type="button" onClick={previewSingle} disabled={!singleUrl.trim()}
            className="shrink-0 inline-flex items-center gap-1.5 text-xs bg-white border border-gray-200 hover:border-gray-300 px-3 py-2 rounded-lg transition disabled:opacity-50">
            <Eye className="w-3.5 h-3.5" /> Preview
          </button>
        </div>
        {preview && (
          <div className="flex items-start gap-3">
            <div className="w-24 h-24 rounded-lg border border-gray-200 overflow-hidden bg-gray-100 shrink-0">
              {!previewErr ? (
                <img src={preview} alt="" className="w-full h-full object-cover" onError={() => setPreviewErr(true)} />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-xs text-gray-400">Cannot load</div>
              )}
            </div>
            <div className="flex flex-col gap-2 pt-1">
              {!previewErr && (
                <button type="button" onClick={addSingle} disabled={adding}
                  className="inline-flex items-center gap-1.5 text-xs bg-[#4F46E5] hover:bg-[#4338CA] text-white px-3 py-2 rounded-lg transition disabled:opacity-50">
                  <Plus className="w-3.5 h-3.5" /> {adding ? "Adding…" : "Add to album"}
                </button>
              )}
              <button type="button" onClick={() => { setPreview(null); setSingleUrl(""); }}
                className="text-xs text-gray-500 hover:text-gray-700">Clear</button>
            </div>
          </div>
        )}
      </div>

      {/* Folder browser */}
      <div className="space-y-3 rounded-xl border border-blue-100 bg-blue-50/40 p-4">
        <label className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
          <FolderOpen className="w-3.5 h-3.5 text-blue-500" /> Google Drive Folder
        </label>
        <p className="text-[10px] text-gray-400">Paste a Google Drive folder URL to browse and select images.</p>
        <div className="flex gap-2">
          <input className={SI} value={folderUrl} onChange={e => setFolderUrl(e.target.value)}
            placeholder="https://drive.google.com/drive/folders/..."
            onKeyDown={e => e.key === "Enter" && fetchFolder()} />
          <button type="button" onClick={fetchFolder} disabled={loading || !folderUrl.trim()}
            className="shrink-0 inline-flex items-center gap-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg transition disabled:opacity-50">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FolderOpen className="w-3.5 h-3.5" />}
            {loading ? "Loading…" : "Browse"}
          </button>
        </div>
        {error && <p className="text-xs text-rose-500">{error}</p>}
        {files.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">{files.length} image{files.length > 1 ? "s" : ""} found — click to select</span>
              <button type="button" onClick={() => setSelected(new Set(files.map(f => f.id)))}
                className="text-[10px] text-indigo-600 hover:underline">Select all</button>
            </div>
            <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
              {files.map(f => (
                <div key={f.id} onClick={() => toggleSelect(f)}
                  className={`relative w-20 h-20 rounded-lg border-2 overflow-hidden bg-gray-50 shrink-0 cursor-pointer ${selected.has(f.id) ? "border-indigo-500 ring-2 ring-indigo-300" : "border-gray-200 hover:border-indigo-300"}`}>
                  <img src={f.thumbnail_url} alt="" className="w-full h-full object-cover"
                    onError={e => { e.target.style.display = "none"; }} />
                  {selected.has(f.id) && (
                    <div className="absolute inset-0 bg-indigo-600/20 flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 text-indigo-700 drop-shadow" />
                    </div>
                  )}
                </div>
              ))}
            </div>
            {selected.size > 0 && (
              <button type="button" onClick={addSelected} disabled={adding}
                className="inline-flex items-center gap-1.5 text-xs bg-[#0F172A] hover:bg-[#1e293b] text-white px-4 py-2 rounded-lg transition disabled:opacity-50">
                <Plus className="w-3.5 h-3.5" /> {adding ? "Adding…" : `Add ${selected.size} selected photo${selected.size > 1 ? "s" : ""}`}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Album Form ─────────────────────────────────────────────────────────────────
function AlbumForm({ f, setF }) {
  const coverRef = useRef();
  const [uploading, setUploading] = useState(false);

  const uploadCover = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/cms/upload", fd);
      setF((prev) => ({ ...prev, cover_image: data.url }));
      toast.success("Cover image uploaded.");
    } catch { toast.error("Cover upload failed."); }
    setUploading(false);
  };

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold text-gray-500 block mb-1">School Name *</label>
          <input className={SI} value={f.school_name} onChange={(e) => setF((p) => ({ ...p, school_name: e.target.value }))} placeholder="e.g. Springfield Elementary" data-testid="album-school-name" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold text-gray-500 block mb-1">Training Name *</label>
          <input className={SI} value={f.training_name} onChange={(e) => setF((p) => ({ ...p, training_name: e.target.value }))} placeholder="e.g. Pedagogy 101" data-testid="album-training-name" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold text-gray-500 block mb-1">Date *</label>
          <input type="date" className={SI} value={f.date} onChange={(e) => setF((p) => ({ ...p, date: e.target.value }))} data-testid="album-date" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold text-gray-500 block mb-1">Location *</label>
          <input className={SI} value={f.location} onChange={(e) => setF((p) => ({ ...p, location: e.target.value }))} placeholder="e.g. Mumbai" data-testid="album-location" />
        </div>
      </div>
      <div>
        <label className="text-xs uppercase tracking-wider font-semibold text-gray-500 block mb-1">Teacher Count</label>
        <input type="number" min={0} className={SI} value={f.teacher_count} onChange={(e) => setF((p) => ({ ...p, teacher_count: parseInt(e.target.value) || 0 }))} data-testid="album-teacher-count" />
      </div>
      <div>
        <label className="text-xs uppercase tracking-wider font-semibold text-gray-500 block mb-1">Cover Image</label>
        <div className="flex items-center gap-3">
          {f.cover_image && (
            <img src={resolve(f.cover_image)} alt="" className="w-16 h-16 rounded-lg object-cover border border-gray-200" />
          )}
          <input className={`${SI} flex-1`} value={f.cover_image} onChange={(e) => setF((p) => ({ ...p, cover_image: e.target.value }))} placeholder="URL or upload…" data-testid="album-cover-url" />
          <input ref={coverRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { const file = e.target.files?.[0]; if (file) uploadCover(file); e.target.value = ""; }} />
          <button onClick={() => coverRef.current.click()} disabled={uploading} className="inline-flex items-center gap-1.5 bg-white border border-gray-200 hover:border-gray-300 text-sm px-3 py-2 rounded-lg whitespace-nowrap" data-testid="album-cover-upload-btn">
            <Upload className="w-3.5 h-3.5" /> {uploading ? "…" : f.cover_image ? "Replace" : "Upload"}
          </button>
        </div>
      </div>
      <div>
        <label className="text-xs uppercase tracking-wider font-semibold text-gray-500 block mb-1">School Logo URL</label>
        <input className={SI} value={f.school_logo} onChange={(e) => setF((p) => ({ ...p, school_logo: e.target.value }))} placeholder="https://…" data-testid="album-school-logo" />
      </div>
    </div>
  );
}

// ── Album Card ─────────────────────────────────────────────────────────────────
function AlbumCard({ album, onDelete, onUpload, onPreview }) {
  const cover = album.cover_image
    ? resolve(album.cover_image)
    : album.photos?.[0]
      ? resolve(album.photos[0])
      : null;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden flex flex-col" data-testid={`album-card-${album.id}`}>
      {/* Cover */}
      <div className="relative aspect-video bg-gray-100">
        {cover ? (
          <img src={cover} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <FolderOpen className="w-10 h-10 text-gray-300" />
          </div>
        )}
        <div className="absolute top-2 right-2 flex gap-1.5">
          {album.photos?.length > 0 && (
            <button
              onClick={() => onPreview(album)}
              className="w-8 h-8 grid place-items-center rounded-full bg-black/50 hover:bg-black/70 text-white backdrop-blur-sm"
              title="Preview photos"
              data-testid={`album-preview-${album.id}`}
            >
              <Eye className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full font-medium backdrop-blur-sm">
          {album.photos?.length || 0} photo{album.photos?.length !== 1 ? "s" : ""}
        </div>
      </div>

      {/* Info */}
      <div className="p-4 flex-1 flex flex-col">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-[#4F46E5] mb-1">{album.training_name}</div>
        <div className="font-semibold text-[#1A202C] text-sm leading-tight">{album.school_name}</div>
        <div className="text-xs text-gray-500 mt-1">{album.date} · {album.location}</div>
        {album.teacher_count > 0 && (
          <div className="text-xs text-gray-400 mt-0.5">{album.teacher_count} teachers</div>
        )}

        {/* Actions */}
        <div className="mt-4 flex gap-2">
          <button
            onClick={() => onUpload(album)}
            className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#4F46E5] hover:bg-[#4338CA] text-white text-xs font-medium px-3 py-2 rounded-lg transition-colors"
            data-testid={`album-upload-btn-${album.id}`}
          >
            <Upload className="w-3.5 h-3.5" /> Upload Photos
          </button>
          <button
            onClick={() => onDelete(album)}
            className="w-8 h-8 grid place-items-center rounded-lg border border-gray-200 hover:border-rose-300 hover:text-rose-600 text-gray-500 transition-colors"
            title="Delete album"
            data-testid={`album-delete-btn-${album.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Photo Grid inside album ────────────────────────────────────────────────────
function AlbumPhotoGrid({ album, onDeletePhoto, onOpenLightbox }) {
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
      {(album.photos || []).map((url, i) => (
        <div key={i} className="relative group aspect-square rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
          <img
            src={resolve(url)}
            alt=""
            className="w-full h-full object-cover cursor-zoom-in"
            onClick={() => onOpenLightbox(i)}
            data-testid={`photo-thumb-${i}`}
          />
          <button
            onClick={() => onDeletePhoto(url)}
            className="absolute top-1 right-1 w-6 h-6 grid place-items-center rounded-full bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-rose-500"
            title="Delete photo"
            data-testid={`photo-delete-${i}`}
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ))}
    </div>
  );
}

// ── Upload Photos Modal (with source toggle) ─────────────────────────────────
function UploadPhotosModal({ active, onClose, onUploaded, onDeletePhoto, onOpenLightbox }) {
  const [source, setSource] = useState("upload"); // "upload" | "drive"
  return (
    <AdminModal
      title="Add Photos"
      subtitle={`${active.school_name} · ${active.training_name}`}
      onClose={onClose}
      maxWidth="max-w-2xl"
      testId="upload-photos-modal"
      footer={
        <button onClick={onClose} className="px-4 py-2.5 text-sm rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors">Done</button>
      }
    >
      <div className="space-y-6">
        {/* Source toggle */}
        <div className="flex gap-2 p-1 bg-gray-100 rounded-xl w-fit">
          {[["upload", "Upload Locally", Upload], ["drive", "Google Drive", FolderOpen]].map(([key, lbl, Icon]) => (
            <button key={key} type="button" onClick={() => setSource(key)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${source === key ? "bg-white text-[#0F172A] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
              <Icon className="w-3.5 h-3.5" /> {lbl}
            </button>
          ))}
        </div>

        {source === "upload" && <PhotoUploadZone albumId={active.id} onUploaded={onUploaded} />}
        {source === "drive" && <DrivePhotoBrowser albumId={active.id} onUploaded={onUploaded} />}

        {/* Show already-uploaded photos */}
        {active.photos?.length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-wider font-semibold text-gray-500 mb-3">
              Existing Photos ({active.photos.length})
            </div>
            <AlbumPhotoGrid
              album={active}
              onDeletePhoto={onDeletePhoto}
              onOpenLightbox={onOpenLightbox}
            />
          </div>
        )}
      </div>
    </AdminModal>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function Gallery() {
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // "create" | "upload" | "preview"
  const [active, setActive] = useState(null); // album for upload/preview
  const [f, setF] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [lightbox, setLightbox] = useState({ album: null, idx: -1 });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/gallery");
      setAlbums(data || []);
    } catch {
      toast.error("Failed to load gallery.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setF(EMPTY_FORM); setModal("create"); };
  const closeModal = () => { setModal(null); setActive(null); };

  const createAlbum = async () => {
    if (!f.school_name.trim()) { toast.error("School name is required."); return; }
    if (!f.training_name.trim()) { toast.error("Training name is required."); return; }
    if (!f.date) { toast.error("Date is required."); return; }
    if (!f.location.trim()) { toast.error("Location is required."); return; }
    setSaving(true);
    try {
      await api.post("/gallery", { ...f, teacher_count: parseInt(f.teacher_count) || 0 });
      toast.success("Album created.");
      closeModal();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create album.");
    }
    setSaving(false);
  };

  const deleteAlbum = async (album) => {
    if (!window.confirm(`Delete album "${album.school_name} – ${album.training_name}" and all its photos?`)) return;
    try {
      await api.delete(`/gallery/${album.id}`);
      toast.success("Album deleted.");
      load();
    } catch {
      toast.error("Failed to delete album.");
    }
  };

  const deletePhoto = async (album, url) => {
    if (!window.confirm("Remove this photo from the album?")) return;
    try {
      await api.delete(`/gallery/${album.id}/photos`, { params: { url } });
      toast.success("Photo removed.");
      // Refresh active album and list
      const { data } = await api.get(`/gallery/${album.id}`);
      setActive(data);
      setAlbums((prev) => prev.map((a) => (a.id === data.id ? data : a)));
    } catch {
      toast.error("Failed to remove photo.");
    }
  };

  const openUpload = (album) => { setActive(album); setModal("upload"); };
  const openPreview = (album) => { setActive(album); setModal("preview"); };

  const handleUploaded = async () => {
    // Refresh the specific album and the full list
    if (active) {
      try {
        const { data } = await api.get(`/gallery/${active.id}`);
        setActive(data);
        setAlbums((prev) => prev.map((a) => (a.id === data.id ? data : a)));
      } catch {
        load();
      }
    }
  };

  const openLightbox = (album, idx) => setLightbox({ album, idx });
  const closeLightbox = () => setLightbox({ album: null, idx: -1 });

  return (
    <div className="min-h-screen" style={{ background: "#F8FAFC" }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">

        {/* Page Header */}
        <Header
          title="Gallery"
          subtitle={`${albums.length} album${albums.length !== 1 ? "s" : ""} · ${albums.reduce((s, a) => s + (a.photos?.length || 0), 0)} photos`}
          actions={
            <button
              onClick={openCreate}
              className="inline-flex items-center gap-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-medium px-4 py-2.5 rounded-xl transition-colors shadow-sm"
              data-testid="create-album-btn"
            >
              <Plus className="w-4 h-4" /> New Album
            </button>
          }
        />

        {/* Album Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-200 overflow-hidden animate-pulse">
                <div className="aspect-video bg-gray-100" />
                <div className="p-4 space-y-2">
                  <div className="h-3 bg-gray-100 rounded w-2/3" />
                  <div className="h-4 bg-gray-100 rounded w-4/5" />
                  <div className="h-3 bg-gray-100 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : albums.length === 0 ? (
          <div className="py-24 flex flex-col items-center text-center" data-testid="gallery-empty">
            <div className="w-16 h-16 rounded-full bg-white border border-gray-200 grid place-items-center mb-4">
              <ImageIcon className="w-7 h-7 text-gray-400" />
            </div>
            <div className="font-semibold text-[#1A202C] text-lg">No albums yet</div>
            <div className="text-sm text-gray-500 mt-1 max-w-xs">
              Create your first album to start uploading training photos.
            </div>
            <button
              onClick={openCreate}
              className="mt-5 inline-flex items-center gap-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-medium px-4 py-2.5 rounded-xl transition-colors"
            >
              <Plus className="w-4 h-4" /> Create First Album
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="album-grid">
            {albums.map((album) => (
              <AlbumCard
                key={album.id}
                album={album}
                onDelete={deleteAlbum}
                onUpload={openUpload}
                onPreview={openPreview}
              />
            ))}
          </div>
        )}
      </div>

      {/* Create Album Modal */}
      {modal === "create" && (
        <AdminModal
          title="Create New Album"
          subtitle="Fill in the album details. You can upload photos after creating."
          onClose={closeModal}
          maxWidth="max-w-xl"
          testId="create-album-modal"
          footer={
            <>
              <button onClick={closeModal} className="px-4 py-2.5 text-sm rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors">Cancel</button>
              <button
                onClick={createAlbum}
                disabled={saving}
                className="inline-flex items-center gap-2 bg-[#4F46E5] hover:bg-[#4338CA] disabled:opacity-60 text-white text-sm font-medium px-5 py-2.5 rounded-xl transition-colors"
                data-testid="save-album-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                {saving ? "Creating…" : "Create Album"}
              </button>
            </>
          }
        >
          <AlbumForm f={f} setF={setF} />
        </AdminModal>
      )}

      {/* Upload Photos Modal */}
      {modal === "upload" && active && (
        <UploadPhotosModal active={active} onClose={closeModal} onUploaded={handleUploaded}
          onDeletePhoto={(url) => deletePhoto(active, url)}
          onOpenLightbox={(i) => openLightbox(active, i)} />
      )}

      {/* Preview Photos Modal */}
      {modal === "preview" && active && (
        <AdminModal
          title={active.school_name}
          subtitle={`${active.training_name} · ${active.date} · ${active.location}`}
          onClose={closeModal}
          maxWidth="max-w-4xl"
          testId="preview-photos-modal"
          footer={
            <div className="flex items-center justify-between w-full gap-3">
              <span className="text-sm text-gray-500">{active.photos?.length || 0} photo{active.photos?.length !== 1 ? "s" : ""}</span>
              <div className="flex gap-2">
                <button
                  onClick={() => { closeModal(); openUpload(active); }}
                  className="inline-flex items-center gap-1.5 bg-[#4F46E5] text-white text-sm font-medium px-4 py-2.5 rounded-xl hover:bg-[#4338CA] transition-colors"
                >
                  <Upload className="w-3.5 h-3.5" /> Upload More
                </button>
                <button onClick={closeModal} className="px-4 py-2.5 text-sm rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors">Close</button>
              </div>
            </div>
          }
        >
          {active.photos?.length === 0 ? (
            <div className="py-12 text-center text-gray-500 text-sm">No photos in this album yet.</div>
          ) : (
            <AlbumPhotoGrid
              album={active}
              onDeletePhoto={(url) => deletePhoto(active, url)}
              onOpenLightbox={(i) => openLightbox(active, i)}
            />
          )}
        </AdminModal>
      )}

      {/* Lightbox */}
      {lightbox.album && lightbox.idx >= 0 && (
        <Lightbox
          photos={lightbox.album.photos || []}
          idx={lightbox.idx}
          onClose={closeLightbox}
        />
      )}
    </div>
  );
}
